import { Container } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useShoppingCart } from 'context/shoppingCartContext';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getAxiosResponseErrorObject } from '@/lib';

// import { withCommonServerSideProps } from '@/lib/withCommonServerSideProps';
import Article from '@/components/Article';
import { H1 } from '@/components/Heading';
import Seo from '@/components/Seo';

import RoutePath from '@/constants/route-path';
import { ProductIdProps } from '@/models/props/ShoppingCartContextProps';
import { ProductService } from '@/services';

const AddToCart: NextPage = () => {
    const intl = useIntl();
    const router = useRouter();
    const session = useSession();
    const {
        basketLocked,
        cartItemsUnlogged,
        increaseItemQuantity,
        updateItemQuantity,
        cartItems,
        updateCartError,
        clearCartUnlogged,
        fetchingData
    } = useShoppingCart();
    const [productsError, setProductsError] = useState<ProductIdProps[]>([]);

    const addMaxItems = useCallback(
        async (codes: ProductIdProps[]) => {
            codes.forEach(async (code) => {
                const productItem = await ProductService.getProductByCode({ code });

                if (productItem && productItem.quantity > 0) {
                    await increaseItemQuantity(code, productItem.quantity);
                }
            });
        },
        [increaseItemQuantity]
    );

    useEffect(() => {
        if (basketLocked) {
            router.push(RoutePath.Home);
            return;
        }

        const addToCartLoggedUser = async () => {
            const promises = cartItemsUnlogged.map(async ({ code, quantity }) => {
                const findItem = cartItems.find((itemInCart) => itemInCart.code === code);

                try {
                    if (findItem) {
                        await updateItemQuantity(code, quantity);
                    } else {
                        await increaseItemQuantity(code, quantity);
                    }
                } catch (e) {
                    const error = getAxiosResponseErrorObject(e);

                    if (e instanceof AxiosError && e?.response?.status === 409) {
                        setProductsError([code, ...productsError]);
                    }

                    const title = intl.formatMessage({
                        id: 'add-to-cart.update-product-quantity.fail'
                    });
                    const description =
                        error?.errors?.length > 0
                            ? intl.formatMessage({ id: error.errors?.[0]?.code })
                            : '';
                    updateCartError({ title, description });
                }
            });

            await Promise.all(promises);
            await clearCartUnlogged();
        };

        const redirectPage = async () => {
            const redirectURL = router.query.redirectURL as string | undefined;
            const path = Object.keys(RoutePath).find(
                (key) => RoutePath[key as keyof typeof RoutePath] === redirectURL
            );

            if (!redirectURL || !path) {
                return router.replace(RoutePath.Home);
            }

            return router.replace(redirectURL);
        };

        const handleGuestCheckout = async () => {
            await redirectPage();
        };

        const updateBasket = async () => {
            if (session.status === 'authenticated' && !fetchingData) {
                await addToCartLoggedUser();
                await redirectPage();
            } else if (session.status === 'unauthenticated') {
                await handleGuestCheckout();
            }
        };

        if (cartItemsUnlogged.length > 0) {
            updateBasket();
        } else if (session.status !== 'loading') {
            redirectPage();
        }
    }, [router]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (productsError.length > 0) {
            addMaxItems(productsError);
        }
    }, [productsError, addMaxItems]);

    return (
        <>
            <Seo
                templateTitle={intl.formatMessage({ id: 'addeding-to-cart' })}
                hideBreadCrumbs={true}
            />
            <Container
                maxW="container.3xl"
                minH="60vh"
                textAlign="center"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <Article py={16}>
                    <H1>
                        <FormattedMessage id="addeding-to-cart" />
                        ...
                    </H1>
                </Article>
            </Container>
        </>
    );
};

// export const getServerSideProps = withCommonServerSideProps(async (context) => {
//     // Allow both authenticated and guest users to access this page
//     return {
//         props: {}
//     };
// });

export default AddToCart;
